import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import type { Card, CardSummary } from '../types';
import { normalizeCard } from './normalize';

interface CueDB extends DBSchema {
  cards: {
    key: string;
    value: Card;
    indexes: { updatedAt: number };
  };
}

const DB_NAME = 'cue';
/** 1: `chats` store. 2: renamed to `cards`, records upgraded to the card shape. */
const DB_VERSION = 2;

let dbPromise: Promise<IDBPDatabase<CueDB>> | null = null;

function db(): Promise<IDBPDatabase<CueDB>> {
  dbPromise ??= openDB<CueDB>(DB_NAME, DB_VERSION, {
    async upgrade(database, oldVersion, _newVersion, tx) {
      const cards = database.createObjectStore('cards', { keyPath: 'id' });
      cards.createIndex('updatedAt', 'updatedAt');
      if (oldVersion >= 1) {
        // The v1 store is not part of the current schema type, so reach it untyped.
        const raw = database as unknown as IDBDatabase;
        if (raw.objectStoreNames.contains('chats')) {
          const oldStore = (tx as unknown as { objectStore(name: string): { getAll(): Promise<unknown[]> } }).objectStore('chats');
          for (const chat of await oldStore.getAll()) await cards.put(normalizeCard(chat));
          raw.deleteObjectStore('chats');
        }
      }
    },
  });
  return dbPromise;
}

export function toSummary(card: Card): CardSummary {
  return { id: card.id, title: card.title, createdAt: card.createdAt, updatedAt: card.updatedAt, language: card.language };
}

/** Newest first. */
export async function listCards(): Promise<CardSummary[]> {
  const summaries: CardSummary[] = [];
  let cursor = await (await db()).transaction('cards').store.index('updatedAt').openCursor(null, 'prev');
  while (cursor) {
    summaries.push(toSummary(cursor.value));
    cursor = await cursor.continue();
  }
  return summaries;
}

export async function getCard(id: string): Promise<Card | undefined> {
  const card = await (await db()).get('cards', id);
  return card ? normalizeCard(card) : undefined;
}

export async function putCard(card: Card): Promise<void> {
  await (await db()).put('cards', card);
}

export async function deleteCard(id: string): Promise<void> {
  await (await db()).delete('cards', id);
}

export async function exportCards(): Promise<Card[]> {
  return (await db()).getAll('cards');
}

export async function importCards(cards: unknown[]): Promise<number> {
  const tx = (await db()).transaction('cards', 'readwrite');
  const normalized = cards.map(normalizeCard);
  await Promise.all([...normalized.map((c) => tx.store.put(c)), tx.done]);
  return normalized.length;
}

export async function clearCards(): Promise<void> {
  await (await db()).clear('cards');
}
